import { useForm } from 'react-hook-form'
import { FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
	DynamicSectionType,
	LeaderboardSectionType,
	LeaderboardUIType,
	GameType,
	GameListSectionType,
} from '@/schema'
import { useEffect } from 'react'

import { Button } from '@/common/ui/button'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Switch } from '@/common/ui/switch'
import {
	DynamicSectionFormValues,
	dynamicSectionFormSchema,
} from './components/FormSchema'
import useQueryGetGameList from '@/modules/game/api/hooks/useQueryGetGameList'
import useQueryGetGameProviderList from '@/modules/game/api/hooks/useQueryGetGameProviderList'
import { DataTableMultiCheckboxFilter } from '@/common/ui/table/data-table-multi-checkbox-filter'
import { GamePicker } from './components/GamePicker'
import { showFormValidationToast } from '@/common/utils/showFormValidationToast'

const GAME_TYPE_TRANSLATIONS: Record<string, string> = {
	SLOT: 'สล็อต',
	FISHING: 'เกมยิงปลา',
	SPORT: 'กีฬา',
	LOTTO: 'หวย',
	CASINO: 'คาสิโน',
}

const GAME_LIST_TYPE_LABELS: Record<string, string> = {
	NEW: 'เกมใหม่',
	POPULAR: 'เกมยอดนิยม',
	CUSTOM: 'เลือกเกมเอง',
}

interface DynamicSectionFormProps {
	defaultValues?: Partial<DynamicSectionFormValues>
	onSubmit: (data: DynamicSectionFormValues) => void
	mode: 'create' | 'edit'
}

export default function DynamicSectionForm({
	defaultValues,
	onSubmit,
	mode,
}: DynamicSectionFormProps) {
	const form = useForm<DynamicSectionFormValues>({
		resolver: zodResolver(dynamicSectionFormSchema),
		defaultValues: {
			name: defaultValues?.name || '',
			type: defaultValues?.type || undefined,
			gameListSection: defaultValues?.gameListSection
				? {
						title: defaultValues.gameListSection.title || '',
						link: defaultValues.gameListSection.link || '',
						gameType: defaultValues.gameListSection.gameType || undefined,
						gameListType:
							defaultValues.gameListSection.gameListType || undefined,
						filterProviderIds:
							defaultValues.gameListSection.filterProviderIds || [],
						gameIds: defaultValues.gameListSection.gameIds || [],
					}
				: {
						title: '',
						link: '',
						gameType: undefined,
						gameListType: undefined,
						filterProviderIds: [],
						gameIds: [],
					},
			lottoAnnouncementSection: defaultValues?.lottoAnnouncementSection || {},
			leaderboardSection: defaultValues?.leaderboardSection
				? {
						title: defaultValues.leaderboardSection.title || '',
						leaderboardType:
							defaultValues.leaderboardSection.leaderboardType ||
							LeaderboardSectionType.DEPOSIT_AMOUNT,
						uiType:
							defaultValues.leaderboardSection.uiType ||
							LeaderboardUIType.VERTICAL,
						limit: defaultValues.leaderboardSection.limit || undefined,
					}
				: {
						title: '',
						leaderboardType: LeaderboardSectionType.DEPOSIT_AMOUNT,
						uiType: LeaderboardUIType.VERTICAL,
						limit: undefined,
					},
			gameSuggestionSection: defaultValues?.gameSuggestionSection
				? {
						title: defaultValues.gameSuggestionSection.title || '',
						limit: defaultValues.gameSuggestionSection.limit || 6,
					}
				: {
						title: '',
						limit: 6,
					},
			footballMatchesSection: defaultValues?.footballMatchesSection
				? {
						title: defaultValues.footballMatchesSection.title || '',
						gameId: defaultValues.footballMatchesSection.gameId || '',
						showLive: defaultValues.footballMatchesSection.showLive ?? true,
						showUpcoming:
							defaultValues.footballMatchesSection.showUpcoming ?? true,
						showFinished:
							defaultValues.footballMatchesSection.showFinished ?? false,
					}
				: {
						title: '',
						gameId: '',
						showLive: true,
						showUpcoming: true,
						showFinished: false,
					},
		},
	})

	const selectedType = form.watch('type')
	const selectedUiType = form.watch('leaderboardSection.uiType')
	const selectedGameListType = form.watch('gameListSection.gameListType')
	const isCustomGameList = selectedGameListType === 'CUSTOM'

	// Fetch all LOTTO games
	const { data: lottoGamesData } = useQueryGetGameList({
		type: 'LOTTO',
		skip: 0,
		take: 1000,
	})

	// Fetch all game providers
	const { data: providersData } = useQueryGetGameProviderList({
		skip: 0,
		take: 1000,
	})

	useEffect(() => {
		if (!selectedType) {
			form.setValue('gameListSection', undefined)
			form.setValue('lottoAnnouncementSection', undefined)
			form.setValue('leaderboardSection', undefined)
			form.setValue('gameSuggestionSection', undefined)
			return
		}

		if (selectedType === DynamicSectionType.GAME_LIST) {
			form.setValue('lottoAnnouncementSection', undefined)
			form.setValue('leaderboardSection', undefined)
			form.setValue('gameSuggestionSection', undefined)
			form.setValue('footballMatchesSection', undefined)
			// Ensure game list section has default values
			if (!form.getValues('gameListSection')) {
				form.setValue('gameListSection', {
					title: '',
					link: '',
					gameType: undefined,
					gameListType: GameListSectionType.NEW,
					filterProviderIds: [],
					gameIds: [],
				})
			}
		} else if (selectedType === DynamicSectionType.LOTTO_ANNOUNCEMENT) {
			form.setValue('gameListSection', undefined)
			form.setValue('leaderboardSection', undefined)
			form.setValue('gameSuggestionSection', undefined)
			form.setValue('footballMatchesSection', undefined)
			// Ensure lotto announcement section has default values
			if (!form.getValues('lottoAnnouncementSection')) {
				form.setValue('lottoAnnouncementSection', {})
			}
		} else if (selectedType === DynamicSectionType.LEADERBOARD) {
			form.setValue('gameListSection', undefined)
			form.setValue('lottoAnnouncementSection', undefined)
			form.setValue('gameSuggestionSection', undefined)
			form.setValue('footballMatchesSection', undefined)
			// Ensure leaderboard section has default values
			if (!form.getValues('leaderboardSection')) {
				form.setValue('leaderboardSection', {
					title: '',
					leaderboardType: LeaderboardSectionType.TOP_WINNERS,
					uiType: LeaderboardUIType.VERTICAL,
					limit: undefined,
				})
			}
		} else if (selectedType === DynamicSectionType.GAME_SUGGESTION) {
			form.setValue('gameListSection', undefined)
			form.setValue('lottoAnnouncementSection', undefined)
			form.setValue('leaderboardSection', undefined)
			form.setValue('footballMatchesSection', undefined)
			// Ensure game suggestion section has default values
			if (!form.getValues('gameSuggestionSection')) {
				form.setValue('gameSuggestionSection', {
					title: '',
					limit: 6,
				})
			}
		} else if (selectedType === DynamicSectionType.FOOTBALL_MATCHES) {
			form.setValue('gameListSection', undefined)
			form.setValue('lottoAnnouncementSection', undefined)
			form.setValue('leaderboardSection', undefined)
			form.setValue('gameSuggestionSection', undefined)
			if (!form.getValues('footballMatchesSection')) {
				form.setValue('footballMatchesSection', {
					title: '',
					gameId: '',
					showLive: true,
					showUpcoming: true,
					showFinished: false,
				})
			}
		}
	}, [selectedType, form])

	// Handle initial state when editing existing data
	useEffect(() => {
		// No additional setup needed for lotto announcement section as it's now empty
	}, [selectedType, lottoGamesData, form])

	const handleSubmit = (values: DynamicSectionFormValues) => {
		// Clean up the data before submitting
		const cleanedValues = {
			...values,
			gameListSection:
				values.type === DynamicSectionType.GAME_LIST
					? values.gameListSection
					: undefined,
			lottoAnnouncementSection:
				values.type === DynamicSectionType.LOTTO_ANNOUNCEMENT
					? values.lottoAnnouncementSection
					: undefined,
			leaderboardSection:
				values.type === DynamicSectionType.LEADERBOARD
					? {
							...values.leaderboardSection!,
							// Set limit to 5 for vertical UI
							limit:
								values.leaderboardSection?.uiType === LeaderboardUIType.VERTICAL
									? 5
									: values.leaderboardSection?.limit,
							uiType:
								values.leaderboardSection?.uiType ?? LeaderboardUIType.VERTICAL,
						}
					: undefined,
			gameSuggestionSection:
				values.type === DynamicSectionType.GAME_SUGGESTION
					? values.gameSuggestionSection
					: undefined,
			footballMatchesSection:
				values.type === DynamicSectionType.FOOTBALL_MATCHES
					? values.footballMatchesSection
					: undefined,
		}
		onSubmit(cleanedValues)
	}

	return (
		<FormProvider {...form}>
			<form
				onSubmit={form.handleSubmit(handleSubmit, showFormValidationToast)}
				className="space-y-8"
			>
				{/* Basic Information Section */}
				<div className="space-y-6 rounded-lg border p-6">
					<h2 className="text-xl font-semibold">ข้อมูลทั่วไป</h2>
					<div className="space-y-4">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>ชื่อ Section</FormLabel>
									<FormControl>
										<Input {...field} placeholder="กรอกชื่อ Section" />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="type"
							render={({ field }) => (
								<FormItem>
									<FormLabel>ประเภท Section</FormLabel>
									<Select
										onValueChange={(value) => {
											field.onChange(value)
											form.clearErrors()
										}}
										value={field.value}
									>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder="เลือกประเภท Section" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											<SelectItem value={DynamicSectionType.GAME_LIST}>
												ตั้งค่าการโชว์เกม
											</SelectItem>
											<SelectItem value={DynamicSectionType.LOTTO_ANNOUNCEMENT}>
												ตั้งค่าการโชว์ประกาศหวย
											</SelectItem>
											<SelectItem value={DynamicSectionType.LEADERBOARD}>
												ตั้งค่าการโชว์ Leaderboard
											</SelectItem>
											<SelectItem value={DynamicSectionType.GAME_SUGGESTION}>
												ตั้งค่าการโชว์เกมแนะนำ
											</SelectItem>
											<SelectItem value={DynamicSectionType.FOOTBALL_MATCHES}>
												ตั้งค่าการโชว์ฟุตบอล
											</SelectItem>
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</div>

				{/* Section Specific Fields */}
				{selectedType === DynamicSectionType.GAME_LIST && (
					<div className="space-y-6 rounded-lg border p-6">
						<h2 className="text-xl font-semibold">ตั้งค่าการโชว์เกม</h2>
						<div className="space-y-4">
							<FormField
								control={form.control}
								name="gameListSection.title"
								render={({ field }) => (
									<FormItem>
										<FormLabel>หัวข้อ</FormLabel>
										<FormControl>
											<Input {...field} placeholder="กรอกหัวข้อ" />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="gameListSection.gameListType"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ประเภทรายการเกม</FormLabel>
										<Select onValueChange={field.onChange} value={field.value}>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="เลือกประเภทรายการเกม" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{Object.values(GameListSectionType).map((type) => (
													<SelectItem key={type} value={type}>
														{GAME_LIST_TYPE_LABELS[type] || type}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>

							{!isCustomGameList && (
								<>
									<FormField
										control={form.control}
										name="gameListSection.gameType"
										render={({ field }) => (
											<FormItem>
												<FormLabel>ประเภทเกม</FormLabel>
												<Select
													onValueChange={field.onChange}
													value={field.value}
												>
													<FormControl>
														<SelectTrigger>
															<SelectValue placeholder="เลือกประเภทเกม" />
														</SelectTrigger>
													</FormControl>
													<SelectContent>
														{Object.values(GameType).map((type) => (
															<SelectItem key={type} value={type}>
																{GAME_TYPE_TRANSLATIONS[type] || type}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="gameListSection.filterProviderIds"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													เลือกผู้ให้บริการ (ไม่เลือกคือแสดงทั้งหมด)
												</FormLabel>
												<FormControl>
													<DataTableMultiCheckboxFilter
														options={
															providersData?.data.map((provider) => ({
																label: provider.name,
																value: provider.id,
															})) || []
														}
														selectedValues={field.value || []}
														onSelectionChange={field.onChange}
														placeholder="เลือกผู้ให้บริการ"
														className="w-full"
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</>
							)}

							{isCustomGameList && (
								<FormField
									control={form.control}
									name="gameListSection.gameIds"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>เลือกเกม</FormLabel>
											<FormControl>
												<GamePicker
													selectedIds={field.value || []}
													onIdsChange={field.onChange}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							)}
						</div>
					</div>
				)}

				{selectedType === DynamicSectionType.LOTTO_ANNOUNCEMENT && (
					<div className="space-y-6 rounded-lg border p-6">
						<h2 className="text-xl font-semibold">ตั้งค่าการโชว์ประกาศหวย</h2>
						<div className="text-muted-foreground">
							ประกาศหวยจะแสดงโดยอัตโนมัติจากระบบ ไม่จำเป็นต้องตั้งค่าเพิ่มเติม
						</div>
					</div>
				)}

				{selectedType === DynamicSectionType.LEADERBOARD && (
					<div className="space-y-6 rounded-lg border p-6">
						<h2 className="text-xl font-semibold">ตั้งค่าการโชว์อันดับ</h2>
						<div className="space-y-4">
							<FormField
								control={form.control}
								name="leaderboardSection.title"
								render={({ field }) => (
									<FormItem>
										<FormLabel>หัวข้อ</FormLabel>
										<FormControl>
											<Input {...field} placeholder="กรอกหัวข้อ" />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="leaderboardSection.leaderboardType"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ประเภทอันดับ</FormLabel>
										<Select onValueChange={field.onChange} value={field.value}>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="เลือกประเภทอันดับ" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{Object.values(LeaderboardSectionType).map((type) => (
													<SelectItem key={type} value={type}>
														{type === 'TOP_WINNERS'
															? 'ผู้ชนะสูงสุด'
															: type === 'DEPOSIT_AMOUNT'
																? 'จำนวนเงินฝาก'
																: 'จำนวนเงินถอน'}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="leaderboardSection.uiType"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ประเภท UI</FormLabel>
										<Select onValueChange={field.onChange} value={field.value}>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="เลือกประเภท UI" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												<SelectItem value={LeaderboardUIType.HORIZONTAL}>
													โชว์ UI แบบเลื่อนแนวนอน
												</SelectItem>
												<SelectItem value={LeaderboardUIType.VERTICAL}>
													แนวตั้ง
												</SelectItem>
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>

							{selectedUiType === LeaderboardUIType.HORIZONTAL && (
								<FormField
									control={form.control}
									name="leaderboardSection.limit"
									render={({ field }) => (
										<FormItem>
											<FormLabel>จำนวน (5-100)</FormLabel>
											<FormControl>
												<Input
													type="number"
													{...field}
													value={field.value ?? ''}
													onChange={(e) => {
														field.onChange(
															e.target.value ? Number(e.target.value) : null,
														)
													}}
													placeholder="กรอกจำนวน 5-100"
													min={5}
													max={100}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							)}
						</div>
					</div>
				)}

				{selectedType === DynamicSectionType.GAME_SUGGESTION && (
					<div className="space-y-6 rounded-lg border p-6">
						<h2 className="text-xl font-semibold">ตั้งค่าการโชว์เกมแนะนำ</h2>
						<div className="space-y-4">
							<FormField
								control={form.control}
								name="gameSuggestionSection.title"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>หัวข้อ</FormLabel>
										<FormControl>
											<Input
												{...field}
												placeholder="กรอกหัวข้อ เช่น แนะนำสำหรับคุณ"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="gameSuggestionSection.limit"
								render={({ field }) => (
									<FormItem>
										<FormLabel>จำนวนเกมที่แนะนำ (1-6)</FormLabel>
										<FormControl>
											<Input
												type="number"
												{...field}
												value={field.value ?? ''}
												onChange={(e) => {
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}}
												placeholder="กรอกจำนวนเกม (ค่าเริ่มต้น 6)"
												min={1}
												max={6}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<div className="text-sm text-muted-foreground">
								เกมที่แนะนำจะถูกคัดเลือกโดยอัตโนมัติจากพฤติกรรมการเล่นของผู้เล่น
								(เกมที่เล่นบ่อย, กำไรสูง, เกมใหม่)
							</div>
						</div>
					</div>
				)}

				{selectedType === DynamicSectionType.FOOTBALL_MATCHES && (
					<div className="space-y-6 rounded-lg border p-6">
						<h2 className="text-xl font-semibold">ตั้งค่าการโชว์ฟุตบอล</h2>
						<div className="space-y-4">
							<FormField
								control={form.control}
								name="footballMatchesSection.title"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>หัวข้อ</FormLabel>
										<FormControl>
											<Input
												{...field}
												placeholder="กรอกหัวข้อ เช่น ฟุตบอลสด"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="footballMatchesSection.gameId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Game ID (สำหรับลิงก์เข้าเกมกีฬา)</FormLabel>
										<FormControl>
											<Input
												{...field}
												placeholder="กรอก Game ID ของเกม SBO/BTI (ไม่บังคับ)"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="footballMatchesSection.showLive"
								render={({ field }) => (
									<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
										<div>
											<FormLabel>แสดงแมตช์ Live</FormLabel>
										</div>
										<FormControl>
											<Switch
												checked={field.value ?? true}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="footballMatchesSection.showUpcoming"
								render={({ field }) => (
									<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
										<div>
											<FormLabel>แสดงแมตช์ที่กำลังจะมาถึง</FormLabel>
										</div>
										<FormControl>
											<Switch
												checked={field.value ?? true}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="footballMatchesSection.showFinished"
								render={({ field }) => (
									<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
										<div>
											<FormLabel>แสดงแมตช์ที่จบแล้ว</FormLabel>
										</div>
										<FormControl>
											<Switch
												checked={field.value ?? false}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
						</div>
					</div>
				)}

				{/* Action Buttons */}
				<div className="flex items-center gap-4 pb-8">
					<Button type="submit" variant="default" className="w-full">
						{mode === 'create' ? 'สร้าง Section' : 'แก้ไข Section'}
					</Button>
				</div>
			</form>
		</FormProvider>
	)
}
